/**
 * Turn a fetch failure into something the operator can act on.
 *
 * A Cloud Function that was never deployed answers the browser's preflight with
 * a 404 carrying no CORS headers, so `fetch` rejects with a bare
 * "TypeError: Failed to fetch" and the console blames CORS. Both readings send
 * you looking at CORS configuration for a function that does not exist. Say so
 * instead — it is by far the likeliest cause, and the other candidates
 * (offline, blocked) are worth naming too.
 */
function describeTransportFailure(functionName, error) {
  if (error && error.name === 'AbortError') return error;
  if (error instanceof TypeError) {
    return new Error(
      `Could not reach "${functionName}". The function may not be deployed, `
      + 'or the request was blocked by the network. Nothing was changed.'
    );
  }
  return error;
}

/** Read the body as JSON, tolerating an error page that is not JSON at all. */
async function readBody(response, functionName) {
  try {
    return await response.json();
  } catch {
    if (!response.ok) {
      throw new Error(`Cloud function "${functionName}" failed (${response.status} ${response.statusText}).`);
    }
    throw new Error(`Cloud function "${functionName}" returned a response that was not JSON.`);
  }
}

export class CloudFunctionsClient {
  constructor(baseUrl, apiKey) {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
  }

  async call(functionName, queryParams = {}, signal = null) {
    if (!this.apiKey) {
      throw new Error('Admin API key not configured. Set VITE_ADMIN_API_KEY in your .env file.');
    }

    const url = new URL(`${this.baseUrl}/${functionName}`);
    Object.entries(queryParams).forEach(([k, v]) => url.searchParams.set(k, v));

    console.log(`[Cloud Function] Calling ${functionName}...`);
    
    const fetchOpts = {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${this.apiKey}` }
    };
    
    if (signal) {
      fetchOpts.signal = signal;
    }

    let response;
    try {
      response = await fetch(url.toString(), fetchOpts);
    } catch (err) {
      throw describeTransportFailure(functionName, err);
    }
    const data = await readBody(response, functionName);

    if (!response.ok) {
      throw new Error(data.error || `Cloud function "${functionName}" failed (${response.status}).`);
    }

    return data;
  }

  async post(functionName, body = {}, queryParams = {}, signal = null) {
    if (!this.apiKey) {
      throw new Error('Admin API key not configured. Set VITE_ADMIN_API_KEY in your .env file.');
    }

    const url = new URL(`${this.baseUrl}/${functionName}`);
    Object.entries(queryParams).forEach(([k, v]) => url.searchParams.set(k, v));

    console.log(`[Cloud Function] POST ${functionName}...`);

    const fetchOpts = {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    };

    if (signal) {
      fetchOpts.signal = signal;
    }

    let response;
    try {
      response = await fetch(url.toString(), fetchOpts);
    } catch (err) {
      throw describeTransportFailure(functionName, err);
    }
    const data = await readBody(response, functionName);

    if (!response.ok) {
      throw new Error(data.error || `Cloud function "${functionName}" failed (${response.status}).`);
    }

    return data;
  }
}
